import { useMemo, useState, type ReactNode } from "react";
import {
  DESIGN_PACK_SYSTEM_DELTA_T_K,
  formatC,
  formatW,
  summariseStoreys,
  type CheckState,
  type DesignCheckContext,
  type DesignCheckResult,
  type HeatPumpUnitSizing,
  type OutdoorUnitCheck,
  type RadiatorUpgrade,
  type RoomDesignCheck,
  type StoreySufficiency,
} from "@sts/calc";
import {
  HEAT_PUMP_DESIGN_LIMITS,
  NOISE_BARRIER_TYPES,
  NOISE_LINES_OF_SIGHT,
  type HeatPumpDesignInputs,
  type NoiseAssessmentPoint,
} from "@sts/rules-data";
import type { PlanObject } from "../api/types.js";
import type { DesignCheckState } from "./use-design-check.js";

/**
 * The MCS design check in the plan's side column: the compact summary at the
 * top of the Objects section, and the details shown with a selected radiator
 * or outdoor unit. Every state is said in words as well as colour — green
 * meets, red does not, amber needs checking on site, grey cannot be assessed
 * yet — and every figure says where it came from. See `computeDesignCheck` in
 * @sts/calc for the rules themselves.
 *
 * Sized by its column like the rest of the panel: content here never re-fits
 * the drawing.
 */

const STATE_WORDS: Readonly<Record<CheckState, string>> = {
  pass: "Meets",
  fail: "Does not meet",
  warn: "Check",
  unknown: "Not assessed",
};

function StateDot({ state }: { readonly state: CheckState }): ReactNode {
  return (
    <span className={`floor-plan__state floor-plan__state--${state}`} aria-hidden="true" title={STATE_WORDS[state]} />
  );
}

/**
 * A design input shown as a value, for someone who may read the design but not
 * change it (an installer on the job). A field, not a disabled input: nothing
 * on screen that looks as if it could be typed into and then refuses.
 */
export function ReadField({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}): ReactNode {
  return (
    <p className="floor-plan__field floor-plan__field--read" data-testid="read-field">
      <span>{label}</span>
      <strong>{value === null || value === undefined || value === "" ? "Not set" : value}</strong>
    </p>
  );
}

/** A number typed and committed on blur/Enter; empty clears it. Read-only: the value, as text. */
function NumberField({
  label,
  value,
  onCommit,
  min,
  max,
  integer = false,
  placeholder,
  disabled,
  ariaLabel,
  readOnly = false,
}: {
  readonly label: string;
  readonly value: number | undefined;
  readonly onCommit: (value: number | undefined) => void;
  readonly min: number;
  readonly max: number;
  readonly integer?: boolean;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
  readonly readOnly?: boolean;
}): ReactNode {
  const [error, setError] = useState<string | null>(null);
  if (readOnly) return <ReadField label={label} value={value === undefined ? null : String(value)} />;
  function commit(input: HTMLInputElement): void {
    const text = input.value.trim();
    if (text === "") {
      setError(null);
      if (value !== undefined) onCommit(undefined);
      return;
    }
    const n = Number(text);
    if (!Number.isFinite(n) || n < min || n > max || (integer && !Number.isInteger(n))) {
      setError(`Enter ${integer ? "a whole number " : ""}from ${min} to ${max}.`);
      input.value = value === undefined ? "" : String(value);
      return;
    }
    setError(null);
    if (n !== value) onCommit(n);
  }
  return (
    <label className="floor-plan__field floor-plan__field--mm">
      <span>{label}</span>
      <input
        aria-label={ariaLabel ?? label}
        inputMode="decimal"
        placeholder={placeholder}
        disabled={disabled}
        key={`${label}:${value ?? ""}`}
        defaultValue={value ?? ""}
        onBlur={(event) => commit(event.target)}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") {
            event.currentTarget.value = value === undefined ? "" : String(value);
            event.currentTarget.blur();
          }
        }}
      />
      {error ? <em className="floor-plan__field-error">{error}</em> : null}
    </label>
  );
}

// ---------------------------------------------------------------- summary --

export interface DesignSummaryProps {
  readonly state: DesignCheckState;
  readonly result: DesignCheckResult | undefined;
  readonly onSelectObject: (id: string) => void;
  readonly onPickRoom: (story: number, index: number) => void;
  /** Set when the plan has been corrected since the heat loss was calculated: what to say. */
  readonly planEdited?: string | null;
  /** May read the design but not change it: the design flow is shown, not offered for editing. */
  readonly readOnly?: boolean;
  /** Names someone typed for storeys, by level — so the per-storey panel calls them what the rest of the app does. */
  readonly storeyLabels?: ReadonlyMap<number, string>;
}

export function DesignCheckSummary({
  state,
  result,
  onSelectObject,
  onPickRoom,
  planEdited,
  readOnly = false,
  storeyLabels,
}: DesignSummaryProps): ReactNode {
  const context = state.context;
  return (
    <section className="floor-plan__design" aria-label="MCS design check" data-testid="design-check">
      <p className="floor-plan__panel-title">MCS design check</p>
      {planEdited && context?.heatLoss.available ? (
        <p className="floor-plan__stale-heat-loss" role="note" data-testid="design-check-plan-edited">
          <strong>Plan edited since the heat loss was calculated.</strong> {planEdited}
        </p>
      ) : null}
      {state.status === "unavailable" ? (
        <p className="floor-plan__panel-empty">The design check needs the survey&apos;s heat loss, which this view does not have.</p>
      ) : state.status === "loading" && !context ? (
        <p className="floor-plan__panel-empty">Loading the survey&apos;s heat loss…</p>
      ) : state.status === "failed" && !context ? (
        <p className="floor-plan__panel-error" role="alert">
          The design check could not be loaded. {state.error}{" "}
          <button type="button" className="floor-plan__link" onClick={state.reload}>
            Try again
          </button>
        </p>
      ) : context && result ? (
        <>
          <FlowEditor state={state} context={context} result={result} readOnly={readOnly} />
          <StoreySufficiencyPanel
            result={result}
            storeyLabels={storeyLabels}
            previewing={state.previewing}
            flowTempC={result.temps?.flowC ?? null}
            onPickRoom={onPickRoom}
          />
          <ul className="floor-plan__design-lines" aria-label="Design check results">
            {result.lines.map((line, i) => {
              const target = line.objectId ?? (line.room ? "room" : undefined);
              const body = (
                <>
                  <StateDot state={line.state} />
                  <span className="visually-hidden">{STATE_WORDS[line.state]}: </span>
                  <span>{line.text}</span>
                </>
              );
              return (
                <li key={`${line.key}-${i}`} className={`floor-plan__design-line floor-plan__design-line--${line.state}`} data-state={line.state} data-key={line.key}>
                  {target ? (
                    <button
                      type="button"
                      className="floor-plan__design-item"
                      onClick={() => {
                        if (line.objectId) onSelectObject(line.objectId);
                        else if (line.room) onPickRoom(line.room.story, line.room.index);
                      }}
                    >
                      {body}
                    </button>
                  ) : (
                    <span className="floor-plan__design-item">{body}</span>
                  )}
                </li>
              );
            })}
          </ul>
          {result.rooms.worstRoom ? (
            <p className="floor-plan__source" data-testid="design-emitter-temperature">
              Design emitter temperature: {formatC(result.rooms.worstRoom.requiredFlowTempC)} flow, set by{" "}
              {result.rooms.worstRoom.name} — the worst performing room (MIS 3005-D §3.8.1(d))
              {result.rooms.worstRoom.complete ? "." : "; some rooms could not be assessed, so a worse one may exist."}
            </p>
          ) : null}
          <RoomList result={result} onPickRoom={onPickRoom} />
          <Recommendations result={result} />
          <p className="floor-plan__fit-note">
            Green meets the rule named; red does not; amber passes on the plan but needs checking on site; grey
            cannot be assessed yet. Each line says which.
          </p>
        </>
      ) : null}
      {state.error && context ? (
        <p className="floor-plan__panel-error" role="alert">
          {state.error}
        </p>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------ flow temperature --

/**
 * Migration 0035's CHECK constraints, which the API validates against too.
 * Anything outside these is refused by the database, so the slider does not
 * offer it.
 */
const FLOW_MIN_C = 25;
const FLOW_MAX_C = 80;
const DELTA_T_MIN_K = 1;
const DELTA_T_MAX_K = 20;
/** Where the slider starts when nothing has been designed yet. It is not a value: see the copy beside it. */
const UNSET_SLIDER_START_C = 45;

/** "14 September 2026, 09:12" — the whole moment, since "who and when" is the point of showing it. */
function whenWords(iso: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * The typed half of the slider. It keeps the TEXT, not the number, because a
 * box bound straight to a validated number cannot be typed into: clearing "45"
 * to type "55" passes through "5", which is below the minimum, so a controlled
 * input would reject it and snap back before the second digit arrived. Here the
 * text is whatever was typed and the value is only pushed up when the text is a
 * number in range.
 */
function TypedNumber({
  ariaLabel,
  value,
  min,
  max,
  step,
  disabled,
  onChange,
}: {
  readonly ariaLabel: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly disabled?: boolean;
  readonly onChange: (value: number) => void;
}): ReactNode {
  const [text, setText] = useState(String(value));
  // Follows the value when it is changed elsewhere (the slider, a save, a
  // reload), but leaves half-typed text alone while it still means this value.
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    if (Number(text) !== value) setText(String(value));
  }
  return (
    <input
      className="floor-plan__slider-number"
      type="number"
      aria-label={ariaLabel}
      inputMode="decimal"
      min={min}
      max={max}
      step={step}
      value={text}
      disabled={disabled}
      onChange={(event) => {
        const raw = event.target.value;
        setText(raw);
        const n = Number(raw);
        if (raw.trim() !== "" && Number.isFinite(n) && n >= min && n <= max) onChange(n);
      }}
      onBlur={() => setText(String(value))}
    />
  );
}

/** A slider and a typed box on the same value, so it can be swept or stated exactly. */
function SliderField({
  label,
  unit,
  value,
  min,
  max,
  step,
  disabled,
  onChange,
  ariaLabel,
}: {
  readonly label: string;
  readonly unit: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly disabled?: boolean;
  readonly onChange: (value: number) => void;
  readonly ariaLabel: string;
}): ReactNode {
  return (
    <div className="floor-plan__slider-field">
      {/* A span, not a <label>: both inputs carry their own aria-label, and a
          <label for> pointing at one of them would give it a second, shorter
          accessible name that says less than the one it already has. */}
      <span className="floor-plan__slider-label">{label}</span>
      <div className="floor-plan__slider-row">
        <input
          type="range"
          aria-label={ariaLabel}
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <TypedNumber
          ariaLabel={`${ariaLabel}, typed`}
          value={value}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onChange={onChange}
        />
        <span className="floor-plan__slider-unit">{unit}</span>
      </div>
    </div>
  );
}

/**
 * The design flow temperature and the system ΔT, as a live control.
 *
 * Moving either re-rates every emitter in the house at once — that is the
 * question this answers: "what flow temperature do these radiators actually
 * work at?". Nothing is written while it moves. The saved figures stay the
 * source of truth for the design check endpoint, the design pack and the
 * installer pack, and the panel says in words which figure is which, because
 * a number on a screen that looks saved and is not is the failure mode worth
 * designing against.
 */
function FlowEditor({
  state,
  context,
  result,
  readOnly,
}: {
  readonly state: DesignCheckState;
  readonly context: DesignCheckContext;
  readonly result: DesignCheckResult;
  readonly readOnly: boolean;
}): ReactNode {
  const savedFlowC = context.designFlowTempC;
  const savedDeltaTK = context.systemDeltaTK;
  const shownFlowC = state.previewContext?.designFlowTempC ?? savedFlowC;
  const shownDeltaTK = state.previewContext?.systemDeltaTK ?? savedDeltaTK;
  const sliderFlowC = shownFlowC ?? UNSET_SLIDER_START_C;
  const sliderDeltaTK = shownDeltaTK ?? DESIGN_PACK_SYSTEM_DELTA_T_K;
  const disabled = state.saving || state.jobId === null;
  const setBy = state.savedBy;
  const setWhen = whenWords(setBy?.at ?? null);

  if (readOnly) {
    return (
      <div className="floor-plan__design-flow" data-testid="design-flow">
        <div className="floor-plan__quick">
          <ReadField label="Design flow (°C)" value={savedFlowC === null ? null : String(savedFlowC)} />
          <ReadField label="Flow − return (K)" value={savedDeltaTK === null ? null : String(savedDeltaTK)} />
        </div>
        <p className={`floor-plan__source floor-plan__source--${result.flow.state}`}>{result.flow.message}</p>
      </div>
    );
  }

  return (
    <div className="floor-plan__design-flow" data-testid="design-flow">
      <p className="floor-plan__panel-subtitle">Design flow temperature</p>
      <SliderField
        label="Flow"
        unit="°C"
        ariaLabel="Design flow temperature, °C"
        value={sliderFlowC}
        min={FLOW_MIN_C}
        max={FLOW_MAX_C}
        step={0.5}
        disabled={disabled}
        onChange={(v) => state.setPreview({ designFlowTempC: v })}
      />
      <SliderField
        label="System ΔT (flow − return)"
        unit="K"
        ariaLabel="System delta T, flow minus return, K"
        value={sliderDeltaTK}
        min={DELTA_T_MIN_K}
        max={DELTA_T_MAX_K}
        step={0.5}
        disabled={disabled}
        onChange={(v) => state.setPreview({ systemDeltaTK: v })}
      />
      <p className="floor-plan__source" data-testid="design-flow-water">
        Return {formatC(sliderFlowC - sliderDeltaTK)}, mean water{" "}
        {formatC(sliderFlowC - sliderDeltaTK / 2)} — what every emitter below is worked out at.
      </p>

      {state.previewing ? (
        <div className="floor-plan__design-preview" role="status" data-testid="design-flow-preview">
          <p className="floor-plan__fit-title">
            <strong>Preview — not saved.</strong>
          </p>
          <p className="floor-plan__fit-note">
            Everything below is worked out at {formatC(sliderFlowC)} flow and {sliderDeltaTK} K. Saved:{" "}
            {savedFlowC === null
              ? "no design flow temperature yet"
              : `${formatC(savedFlowC)} flow`}
            {savedDeltaTK === null ? ", ΔT not set" : `, ${savedDeltaTK} K`}. The design check, the design
            pack and the installer pack keep using the saved figures until you save these.
          </p>
          <div className="floor-plan__selected-actions">
            <button
              type="button"
              className="floor-plan__tool floor-plan__tool--primary"
              disabled={disabled}
              onClick={() =>
                state.setDesignFlow({ designFlowTempC: sliderFlowC, systemDeltaTK: sliderDeltaTK })
              }
            >
              Save {formatC(sliderFlowC)} flow and {sliderDeltaTK} K
            </button>
            <button
              type="button"
              className="floor-plan__tool"
              disabled={disabled}
              onClick={state.clearPreview}
            >
              Back to saved
            </button>
          </div>
        </div>
      ) : (
        <p className="floor-plan__source" data-testid="design-flow-saved">
          {savedFlowC === null
            ? "No design flow temperature saved yet — move the slider to try one, then save it."
            : `Saved: ${formatC(savedFlowC)} flow${savedDeltaTK === null ? ", ΔT not set (5 K assumed)" : `, ${savedDeltaTK} K`}.`}
          {setBy && setWhen
            ? ` Set by ${setBy.name ?? "someone no longer on this account"} on ${setWhen}.`
            : savedFlowC === null
              ? ""
              : " Who saved it was not recorded."}
        </p>
      )}

      <p className={`floor-plan__source floor-plan__source--${result.flow.state}`}>{result.flow.message}</p>
    </div>
  );
}

// ----------------------------------------------------- storey sufficiency --

const STOREY_ROOM_WORDS: Readonly<Record<StoreySufficiency["rooms"][number]["status"], string>> = {
  sufficient: "Meets its heat loss",
  short: "Short",
  "no-emitter": "No emitter",
  "not-assessed": "Not assessed",
  "not-heated": "Not a heated room",
  "no-heat-loss": "No heat loss",
};

/**
 * Storey by storey, what the emitters deliver at the temperature on the slider
 * and how many rooms that is enough for — the panel that moves as the slider
 * does. `summariseStoreys` in @sts/calc does the counting; the rules for which
 * rooms count are documented there.
 */
function StoreySufficiencyPanel({
  result,
  storeyLabels,
  previewing,
  flowTempC,
  onPickRoom,
}: {
  readonly result: DesignCheckResult;
  readonly storeyLabels?: ReadonlyMap<number, string>;
  readonly previewing: boolean;
  readonly flowTempC: number | null;
  readonly onPickRoom: (story: number, index: number) => void;
}): ReactNode {
  const storeys = useMemo(
    () => summariseStoreys(result.rooms.rooms, storeyLabels ? { storeyLabels } : {}),
    [result, storeyLabels],
  );
  if (storeys.length === 0) return null;

  return (
    <section className="floor-plan__design-storeys" aria-label="Emitters by storey" data-testid="design-storeys">
      <p className="floor-plan__panel-subtitle">
        Emitters {flowTempC === null ? "— flow temperature not set" : `at ${formatC(flowTempC)} flow`}
        {previewing ? " (preview)" : ""}
      </p>
      {storeys.map((storey) => (
        <details
          key={storey.story}
          className={`floor-plan__design-storey floor-plan__design-storey--${storey.state}`}
          data-testid="design-storey"
          data-storey={storey.story}
          data-state={storey.state}
          open={storey.state === "fail"}
        >
          <summary>
            <StateDot state={storey.state} />
            <span className="visually-hidden">{STATE_WORDS[storey.state]}: </span>
            <strong>{storey.label}</strong> — {storey.summary}
          </summary>
          <ul className="floor-plan__design-lines" aria-label={`Rooms on ${storey.label}`}>
            {storey.rooms.map((room) => (
              <li
                key={`${room.story}:${room.index}`}
                className={`floor-plan__design-line floor-plan__design-line--${room.state}`}
                data-state={room.state}
                data-status={room.status}
              >
                <button
                  type="button"
                  className="floor-plan__design-item"
                  onClick={() => onPickRoom(room.story, room.index)}
                >
                  <StateDot state={room.state} />
                  <span className="visually-hidden">{STOREY_ROOM_WORDS[room.status]}: </span>
                  <span>{room.summary}</span>
                </button>
              </li>
            ))}
          </ul>
          {storey.roomsWithoutEmitter.length > 0 ? (
            <p className="floor-plan__fit-note">
              No emitter on the plan in {storey.roomsWithoutEmitter.join(", ")}. Counted as short, not as
              passing.
            </p>
          ) : null}
          {storey.roomsNotHeated.length > 0 ? (
            <p className="floor-plan__fit-note">
              {storey.roomsNotHeated.join(", ")} {storey.roomsNotHeated.length === 1 ? "has" : "have"} no
              heat loss recorded, so {storey.roomsNotHeated.length === 1 ? "it is" : "they are"} left out of
              the count rather than passed.
            </p>
          ) : null}
          {storey.roomsNotAssessed.length > 0 ? (
            <p className="floor-plan__fit-note floor-plan__fit-note--unknown">
              Not assessed: {storey.roomsNotAssessed.join(", ")}. Counted, but not as passing.
            </p>
          ) : null}
          {storey.roomsWithoutHeatLoss.length > 0 ? (
            <p className="floor-plan__fit-note floor-plan__fit-note--unknown">
              No heat loss for {storey.roomsWithoutHeatLoss.join(", ")} — left out of the count.
            </p>
          ) : null}
        </details>
      ))}
    </section>
  );
}

function RoomList({
  result,
  onPickRoom,
}: {
  readonly result: DesignCheckResult;
  readonly onPickRoom: (story: number, index: number) => void;
}): ReactNode {
  if (result.rooms.rooms.length === 0) return null;
  return (
    <details className="floor-plan__design-rooms">
      <summary>Rooms ({result.rooms.rooms.length})</summary>
      <ul className="floor-plan__design-lines">
        {result.rooms.rooms.map((room) => (
          <li key={`${room.story}:${room.index}`} className={`floor-plan__design-line floor-plan__design-line--${room.state}`} data-state={room.state}>
            <button type="button" className="floor-plan__design-item" onClick={() => onPickRoom(room.story, room.index)}>
              <StateDot state={room.state} />
              <span className="visually-hidden">{STATE_WORDS[room.state]}: </span>
              <span>{room.message}</span>
            </button>
          </li>
        ))}
        {result.rooms.unplaced.map((r) => (
          <li key={r.objectId} className="floor-plan__design-line floor-plan__design-line--warn" data-state="warn">
            <span className="floor-plan__design-item">
              <StateDot state="warn" /> Radiator {r.label} is not in any scanned room, so it counts towards none.
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function Recommendations({ result }: { readonly result: DesignCheckResult }): ReactNode {
  const hp = result.heatPump;
  if (hp.requiredW === undefined || hp.flowC === undefined) return null;
  return (
    <details className="floor-plan__design-rooms" data-testid="design-recommendations">
      <summary>Which heat pump size?</summary>
      <p className="floor-plan__source">{hp.message}</p>
      <p className="floor-plan__source">{hp.hotWaterNote}</p>
      {hp.recommendations.length > 0 ? (
        <ul className="floor-plan__design-lines">
          {hp.recommendations.map((r) => (
            <li key={r.catalogueId} className="floor-plan__design-line">
              <span className="floor-plan__design-item">
                {r.name}: {formatW(r.prefill.capacityW)} ({r.prefill.basis === "max" ? "maximum output" : "EN 14511 rated"}
                {r.prefill.interpolated ? ", interpolated" : ""})
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="floor-plan__source">No catalogue unit has a usable datasheet output at these conditions that meets it.</p>
      )}
      {hp.recommendationsNote ? <p className="floor-plan__fit-note">{hp.recommendationsNote}</p> : null}
      {hp.notes.map((n, i) => (
        <p key={i} className="floor-plan__fit-note floor-plan__fit-note--unknown">
          {n}
        </p>
      ))}
    </details>
  );
}

// ------------------------------------------------------------- radiators --

export function RadiatorDesignDetails({
  room,
  saving,
  onApply,
}: {
  readonly room: RoomDesignCheck | undefined;
  readonly saving: boolean;
  /** Absent for someone who may not change the design: the sizes are listed, not offered. */
  readonly onApply?: (upgrade: RadiatorUpgrade) => void;
}): ReactNode {
  if (!room) {
    return (
      <p className="floor-plan__fit-note floor-plan__fit-note--unknown" data-testid="radiator-design">
        Not in any scanned room, so it is not counted towards any room&apos;s heat loss.
      </p>
    );
  }
  return (
    <div className={`floor-plan__design-box floor-plan__design-box--${room.state}`} data-testid="radiator-design">
      <p className="floor-plan__fit-title">
        <StateDot state={room.state} /> {room.state === "fail" ? "Too small for the room" : room.state === "pass" ? "Heats the room" : "Not assessed"}
      </p>
      <p className="floor-plan__fit-problem">{room.message}</p>
      {room.upgrades.length > 0 && !onApply ? (
        <p className="floor-plan__fit-note">Bigger sizes that fit: {room.upgrades.map((u) => u.label).join(", ")}.</p>
      ) : null}
      {room.upgrades.length > 0 && onApply ? (
        <div className="floor-plan__fit-suggestions" role="group" aria-label="Bigger sizes that fit">
          {room.upgrades.map((u) => (
            <button
              key={`${u.objectId}-${u.label}`}
              type="button"
              className="floor-plan__tool floor-plan__tool--primary"
              disabled={saving}
              onClick={() => onApply(u)}
            >
              {u.label}
            </button>
          ))}
        </div>
      ) : null}
      {room.addRadiatorNote ? <p className="floor-plan__fit-note">{room.addRadiatorNote}</p> : null}
      {room.smallestSingle ? (
        <p className="floor-plan__fit-note">
          Smallest single radiator that would heat it alone: {room.smallestSingle.label} ({formatW(room.smallestSingle.outputW)} at these temperatures).
        </p>
      ) : null}
      {room.notes.map((n, i) => (
        <p key={i} className="floor-plan__fit-note">
          {n}
        </p>
      ))}
      {room.radiators.some((r) => r.working.length > 0) ? (
        <details>
          <summary className="floor-plan__fit-note">Working</summary>
          <pre className="floor-plan__working">{room.radiators.flatMap((r) => r.working).join("\n")}</pre>
        </details>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------- heat pump --

const BARRIER_WORDS: Readonly<Record<NoiseAssessmentPoint["barrierType"], string>> = {
  none: "No barrier",
  "type-1": "Type 1 — solid, ≥18 mm",
  "type-2": "Type 2 — solid, <18 mm",
};
const SIGHT_WORDS: Readonly<Record<NoiseAssessmentPoint["lineOfSight"], string>> = {
  "full-view": "Full view",
  "partial-view": "Partial view",
  "no-view": "No view",
};

function newPositionId(): string {
  return `p-${Math.random().toString(36).slice(2, 10)}`;
}

export interface HeatPumpDesignProps {
  readonly object: Extract<PlanObject, { kind: "heat-pump" }>;
  readonly context: DesignCheckContext | null;
  readonly sizing: HeatPumpUnitSizing | undefined;
  readonly unit: OutdoorUnitCheck | undefined;
  readonly saving: boolean;
  readonly onDesign: (id: string, design: HeatPumpDesignInputs) => void;
  /** Start placing a position on the plan: an existing one to move, or a new one. */
  readonly onPlacePosition: (unitId: string, positionId: string, isNew: boolean) => void;
  readonly placingPositionId: string | null;
  /**
   * May read the design but not change it (an installer on the job): every
   * input is shown as its value, and nothing that would save is offered.
   */
  readonly readOnly?: boolean;
}

export function HeatPumpDesignEditor(props: HeatPumpDesignProps): ReactNode {
  const { object, context, sizing, unit, saving } = props;
  const readOnly = props.readOnly === true;
  const design: HeatPumpDesignInputs = object.spec.design ?? {};
  const set = (patch: Partial<HeatPumpDesignInputs>): void => {
    const next: Record<string, unknown> = { ...design, ...patch };
    for (const key of Object.keys(next)) if (next[key] === undefined) delete next[key];
    props.onDesign(object.id, next as HeatPumpDesignInputs);
  };
  const L = HEAT_PUMP_DESIGN_LIMITS;
  const conditions =
    context && context.externalDesignTempC !== null && context.designFlowTempC !== null
      ? { outdoorC: context.externalDesignTempC, flowC: context.designFlowTempC }
      : undefined;
  const positions = design.noisePositions ?? [];
  const setPosition = (id: string, patch: Partial<NoiseAssessmentPoint>): void =>
    set({ noisePositions: positions.map((p) => (p.id === id ? { ...p, ...patch } : p)) });

  return (
    <div className="floor-plan__design-hp" data-testid="heat-pump-design">
      <p className="floor-plan__panel-subtitle">MCS design check — this unit</p>
      {unit?.nominal ? <p className="floor-plan__source">Nominal {unit.nominal}.</p> : null}

      {/* ---- Capacity at design conditions (MIS 3005-D §3.4.1(e)) ---- */}
      <div className={`floor-plan__design-box floor-plan__design-box--${sizing?.state ?? "unknown"}`} data-testid="heat-pump-sizing">
        <p className="floor-plan__fit-title">
          <StateDot state={sizing?.state ?? "unknown"} /> Size
        </p>
        {sizing ? <p className="floor-plan__fit-problem">{sizing.message}</p> : null}
        <NumberField
          label={conditions ? `Output at ${formatC(conditions.outdoorC)} / ${formatC(conditions.flowC)} flow (W)` : "Output at design conditions (W)"}
          ariaLabel="Heat pump output at design conditions, W"
          value={design.capacityAtDesignW}
          min={L.capacityW.min}
          max={L.capacityW.max}
          disabled={saving || !conditions}
          readOnly={readOnly}
          placeholder="from the MCS Product Directory"
          onCommit={(v) =>
            conditions &&
            set({
              capacityAtDesignW: v,
              capacityConditions: v === undefined ? undefined : conditions,
              capacitySource: v === undefined ? undefined : design.capacitySource ?? "Entered by the designer",
            })
          }
        />
        {!conditions && !readOnly ? (
          <p className="floor-plan__fit-note">Set the design flow temperature first: the output is entered for it.</p>
        ) : null}
        {design.capacitySource ? <p className="floor-plan__source">Source: {design.capacitySource}</p> : null}
        {!readOnly && sizing?.prefill && conditions && design.capacityAtDesignW !== sizing.prefill.capacityW ? (
          <div className="floor-plan__design-prefill">
            <button
              type="button"
              className="floor-plan__tool"
              disabled={saving}
              onClick={() =>
                set({
                  capacityAtDesignW: sizing.prefill!.capacityW,
                  capacityConditions: conditions,
                  capacitySource: `${sizing.prefill!.label}: ${sizing.prefill!.derivation} ${sizing.prefill!.citations.join("; ")}`.slice(0, L.maxText),
                })
              }
            >
              Use {formatW(sizing.prefill.capacityW)} from the datasheet
            </button>
            <p className="floor-plan__fit-note">
              {sizing.prefill.label}. {sizing.prefill.derivation} {sizing.prefill.caveats.join(" ")} Source:{" "}
              {sizing.prefill.citations.join("; ")}
            </p>
          </div>
        ) : sizing?.prefillUnavailable ? (
          <p className="floor-plan__fit-note">{sizing.prefillUnavailable}</p>
        ) : null}
      </div>

      {/* ---- Placement: wall, clearances ---- */}
      {unit ? (
        <div className={`floor-plan__design-box floor-plan__design-box--${worstOf([unit.wall.state, unit.clearances.state])}`} data-testid="heat-pump-placement">
          <p className="floor-plan__fit-title">
            <StateDot state={worstOf([unit.wall.state, unit.clearances.state])} /> Against its wall
          </p>
          <p className="floor-plan__fit-note">{unit.wall.message}</p>
          {unit.clearances.items.map((item, i) => (
            <p key={i} className={`floor-plan__design-check floor-plan__design-check--${item.state}`}>
              <StateDot state={item.state} /> {item.message}
            </p>
          ))}
          {unit.clearances.openings.map((o) => (
            <p key={o.id} className="floor-plan__design-check floor-plan__design-check--warn">
              <StateDot state="warn" /> {o.message}
            </p>
          ))}
          {unit.clearances.aboveNote ? <p className="floor-plan__fit-note">{unit.clearances.aboveNote}</p> : null}
          <p className="floor-plan__source">Clearances from: {unit.clearances.source}.</p>
          <NumberField
            label="Wall thickness (mm)"
            ariaLabel="Thickness of the wall behind the unit, mm"
            value={design.wallThicknessMm}
            min={L.wallThicknessMm.min}
            max={L.wallThicknessMm.max}
            integer
            placeholder="not in the scan"
            readOnly={readOnly}
            disabled={saving}
            onCommit={(v) => set({ wallThicknessMm: v })}
          />
          <details>
            <summary className="floor-plan__fit-note">Your clearances (from the installation manual)</summary>
            <div className="floor-plan__custom">
              {(["back", "front", "left", "right"] as const).map((side) => (
                <NumberField
                  key={side}
                  label={`${side[0]!.toUpperCase()}${side.slice(1)} (mm)`}
                  ariaLabel={`${side} clearance, mm`}
                  value={design.clearancesMm?.[side]}
                  min={L.clearanceMm.min}
                  max={L.clearanceMm.max}
                  integer
                  readOnly={readOnly}
                  disabled={saving}
                  onCommit={(v) => {
                    const next = { ...(design.clearancesMm ?? {}), [side]: v };
                    if (v === undefined) delete (next as Record<string, unknown>)[side];
                    set({ clearancesMm: Object.keys(next).length > 0 ? next : undefined });
                  }}
                />
              ))}
            </div>
          </details>
        </div>
      ) : null}

      {/* ---- MCS 020 a) ---- */}
      {unit ? (
        <div className={`floor-plan__design-box floor-plan__design-box--${unit.noise.state}`} data-testid="heat-pump-noise">
          <p className="floor-plan__fit-title">
            <StateDot state={unit.noise.state} /> Noise — {unit.noise.rulesetLabel}
          </p>
          <p className="floor-plan__fit-problem">{unit.noise.message}</p>
          <NumberField
            label="Sound power, EN 12102 (dB(A))"
            ariaLabel="Sound power level, dB(A)"
            value={design.soundPowerDbA}
            min={L.soundPowerDbA.min}
            max={L.soundPowerDbA.max}
            disabled={saving}
            readOnly={readOnly}
            placeholder="not low-noise mode"
            onCommit={(v) =>
              set({ soundPowerDbA: v, soundPowerSource: v === undefined ? undefined : design.soundPowerSource ?? "Entered by the designer" })
            }
          />
          {!readOnly && unit.noise.soundPowerPrefill && design.soundPowerDbA === undefined ? (
            <div className="floor-plan__design-prefill">
              <button
                type="button"
                className="floor-plan__tool"
                disabled={saving}
                onClick={() =>
                  set({
                    soundPowerDbA: unit.noise.soundPowerPrefill!.dbA,
                    soundPowerSource: `${unit.noise.soundPowerPrefill!.label}: ${unit.noise.soundPowerPrefill!.condition}; ${unit.noise.soundPowerPrefill!.citation}`.slice(0, L.maxText),
                  })
                }
              >
                Use {unit.noise.soundPowerPrefill.dbA} dB(A) from the datasheet
              </button>
              <p className="floor-plan__fit-note">
                {unit.noise.soundPowerPrefill.label}. {unit.noise.soundPowerPrefill.condition} Source:{" "}
                {unit.noise.soundPowerPrefill.citation}
              </p>
            </div>
          ) : null}
          {design.soundPowerSource ? <p className="floor-plan__source">Source: {design.soundPowerSource}</p> : null}
          {readOnly ? (
            <div className="floor-plan__quick">
              <ReadField
                label="Mounted"
                value={
                  design.mounting === "wall-bracket"
                    ? "On wall brackets"
                    : design.mounting === "ground"
                      ? "On the ground"
                      : "Not stated (ground assumed)"
                }
              />
              <ReadField
                label="Directivity Q"
                value={
                  design.directivityOverride !== undefined
                    ? `Q${design.directivityOverride}`
                    : `From the plan (Q${unit.directivity.derivedQ})`
                }
              />
            </div>
          ) : (
          <div className="floor-plan__quick">
            <label className="floor-plan__field">
              <span>Mounted</span>
              <select
                aria-label="How the unit is mounted"
                value={design.mounting ?? ""}
                disabled={saving}
                onChange={(e) => set({ mounting: e.target.value === "" ? undefined : (e.target.value as "ground" | "wall-bracket") })}
              >
                <option value="">Not stated (ground assumed)</option>
                <option value="ground">On the ground</option>
                <option value="wall-bracket">On wall brackets</option>
              </select>
            </label>
            <label className="floor-plan__field">
              <span>Directivity Q</span>
              <select
                aria-label="Directivity factor Q"
                value={design.directivityOverride ?? ""}
                disabled={saving}
                onChange={(e) =>
                  set({ directivityOverride: e.target.value === "" ? undefined : (Number(e.target.value) as 2 | 4 | 8) })
                }
              >
                <option value="">From the plan (Q{unit.directivity.derivedQ})</option>
                <option value="2">Q2 — one surface</option>
                <option value="4">Q4 — two surfaces</option>
                <option value="8">Q8 — three surfaces</option>
              </select>
            </label>
          </div>
          )}
          <p className={`floor-plan__design-check floor-plan__design-check--${unit.directivity.state}`}>
            <StateDot state={unit.directivity.state} /> {unit.directivity.message}
          </p>
          {unit.directivity.surfaces
            .filter((s) => s.note)
            .map((s, i) => (
              <p key={i} className="floor-plan__fit-note">
                {s.note}
              </p>
            ))}
          {unit.directivity.notes.map((n, i) => (
            <p key={`n${i}`} className="floor-plan__fit-note">
              {n}
            </p>
          ))}
          {unit.noise.notes.map((n, i) => (
            <p key={`nn${i}`} className="floor-plan__fit-note floor-plan__fit-note--unknown">
              {n}
            </p>
          ))}

          <p className="floor-plan__panel-subtitle">Neighbours&apos; windows</p>
          {positions.length === 0 && readOnly ? (
            <p className="floor-plan__fit-note">No neighbour&apos;s window has been assessed for this unit yet.</p>
          ) : null}
          {positions.length === 0 && !readOnly ? (
            <p className="floor-plan__fit-note">
              MCS 020 a) assesses 1 m outside the centre of each neighbour&apos;s habitable-room window or door. Add each
              one that could be affected — the nearest is not always the loudest.
            </p>
          ) : null}
          {positions.map((p) => {
            const result = unit.noise.positions.find((r) => r.id === p.id);
            return (
              <div key={p.id} className={`floor-plan__design-position floor-plan__design-check--${result?.state ?? "unknown"}`} data-testid="noise-position-row">
                {readOnly ? (
                  <div className="floor-plan__quick">
                    <ReadField label="Where" value={p.description} />
                    <ReadField label="Barrier" value={BARRIER_WORDS[p.barrierType]} />
                    <ReadField label="Line of sight" value={SIGHT_WORDS[p.lineOfSight]} />
                    <ReadField
                      label="Measured distance (m)"
                      value={p.distanceM !== null ? String(p.distanceM) : p.point ? "from the plan" : null}
                    />
                  </div>
                ) : (
                <>
                <label className="floor-plan__field">
                  <span>Where</span>
                  <input
                    aria-label="Assessment position description"
                    placeholder="Address, window, floor"
                    maxLength={L.maxText}
                    disabled={saving}
                    key={`${p.id}:${p.description}`}
                    defaultValue={p.description}
                    onBlur={(e) => {
                      const text = e.target.value.trim();
                      if (text !== p.description) setPosition(p.id, { description: text });
                    }}
                  />
                </label>
                <div className="floor-plan__quick">
                  <label className="floor-plan__field">
                    <span>Barrier</span>
                    <select
                      aria-label="Barrier between the unit and this window"
                      value={p.barrierType}
                      disabled={saving}
                      onChange={(e) => setPosition(p.id, { barrierType: e.target.value as NoiseAssessmentPoint["barrierType"] })}
                    >
                      {NOISE_BARRIER_TYPES.map((b) => (
                        <option key={b} value={b}>
                          {BARRIER_WORDS[b]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="floor-plan__field">
                    <span>Line of sight</span>
                    <select
                      aria-label="Line of sight from the top of the unit"
                      value={p.lineOfSight}
                      disabled={saving}
                      onChange={(e) => setPosition(p.id, { lineOfSight: e.target.value as NoiseAssessmentPoint["lineOfSight"] })}
                    >
                      {NOISE_LINES_OF_SIGHT.map((l) => (
                        <option key={l} value={l}>
                          {SIGHT_WORDS[l]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <NumberField
                    label="Measured distance (m)"
                    ariaLabel="Measured distance to this window, m"
                    value={p.distanceM ?? undefined}
                    min={L.distanceM.min}
                    max={L.distanceM.max}
                    disabled={saving}
                    placeholder={p.point ? "from the plan" : "type it"}
                    onCommit={(v) => setPosition(p.id, { distanceM: v ?? null })}
                  />
                </div>
                </>
                )}
                {result ? (
                  <p className="floor-plan__design-check">
                    <StateDot state={result.state} /> {result.message}
                  </p>
                ) : null}
                {result && result.working.length > 0 ? (
                  <details>
                    <summary className="floor-plan__fit-note">Working</summary>
                    <pre className="floor-plan__working">{result.working.join("\n")}</pre>
                  </details>
                ) : null}
                {readOnly ? null : (
                <div className="floor-plan__selected-actions">
                  <button
                    type="button"
                    className="floor-plan__tool"
                    aria-pressed={props.placingPositionId === p.id}
                    disabled={saving}
                    onClick={() => props.onPlacePosition(object.id, p.id, false)}
                  >
                    {p.point ? "Move on the plan" : "Place on the plan"}
                  </button>
                  <button
                    type="button"
                    className="floor-plan__tool floor-plan__tool--danger"
                    disabled={saving}
                    onClick={() => set({ noisePositions: positions.filter((q) => q.id !== p.id) })}
                  >
                    Remove
                  </button>
                </div>
                )}
              </div>
            );
          })}
          {readOnly ? null : (
          <div className="floor-plan__selected-actions">
            <button
              type="button"
              className="floor-plan__tool floor-plan__tool--primary"
              disabled={saving || positions.length >= L.maxNoisePositions}
              aria-pressed={props.placingPositionId !== null && !positions.some((p) => p.id === props.placingPositionId)}
              onClick={() => props.onPlacePosition(object.id, newPositionId(), true)}
            >
              Place a neighbour&apos;s window
            </button>
            <button
              type="button"
              className="floor-plan__tool"
              disabled={saving || positions.length >= L.maxNoisePositions}
              onClick={() =>
                set({
                  noisePositions: [
                    ...positions,
                    { id: newPositionId(), description: "", point: null, distanceM: null, barrierType: "none", lineOfSight: "full-view" },
                  ],
                })
              }
            >
              Add one by distance
            </button>
          </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function worstOf(states: readonly CheckState[]): CheckState {
  return states.includes("fail") ? "fail" : states.includes("unknown") ? "unknown" : states.includes("warn") ? "warn" : "pass";
}
